import {
	Scene,
	Camera,
	ArcRotateCamera,
	Observable,
	Vector3,
	Tools,
	Animation,
} from '@babylonjs/core';
import ObservablesManager from './ObservablesManager';
import { CameraConfiguration } from '../configuration/interfaces/CameraConfiguration';
import DEFAULTS from '../configuration/defaults/DefaultCameraConfiguration';
import { ArcRotateCameraTouchInput } from './CameraManager/ArcRotateCameraTouchInput';
import { ArcRotateCameraPointerInput } from './CameraManager/ArcRotateCameraPointerInput';

export default class CameraManager {
	private scene: Scene;
	private observablesManager: ObservablesManager;
	private activeCameraId: string | null;
	private cameras = new Map<string, ArcRotateCamera>();

	constructor(scene: Scene, observablesManager: ObservablesManager) {
		this.scene = scene;
		this.observablesManager = observablesManager;
	}

	/**
	 * Will notify when the camera was created.
	 */
	public get onCameraCreated(): Observable<Camera> {
		return this.observablesManager.onCameraCreatedObservable;
	}

	/**
	 * Creates camera.
	 */
	public createCamera(
		canvas: HTMLCanvasElement,
		id = 'Camera',
		attachControl = true,
		configuration?: CameraConfiguration
	): void {
		if (this.cameras.has(id)) {
			this.destroyCamera(id);
		}

		const config = configuration ? this.parseConfiguration(configuration) : DEFAULTS;

		const camera = new ArcRotateCamera(
			id,
			config.alpha as number,
			config.beta as number,
			config.radius as number,
			config.position as Vector3,
			this.scene
		);
		camera.useFramingBehavior = config.useFramingBehavior as boolean;
		if (camera.framingBehavior && camera.useFramingBehavior) {
			camera.framingBehavior.framingTime = config.framingTime as number;
			camera.framingBehavior.elevationReturnTime = config.elevationReturnTime as number;

			const key = '_applyUserInteraction';
			camera.framingBehavior[key] = (): void => {
				// prevent camera from stoping animation on user interaction
			};
		}

		const badOS = this.scene.getEngine()['_badOS'];
		const inputManager = camera.inputs;
		inputManager.removeByType('ArcRotateCameraMouseWheelInput');
		inputManager.removeByType('ArcRotateCameraKeyboardMoveInput');
		camera.inputs.removeByType('ArcRotateCameraPointersInput');
		if (badOS) {
			camera.inputs.add(new ArcRotateCameraTouchInput());
		} else {
			camera.inputs.add(new ArcRotateCameraPointerInput());
		}
		this.cameras.set(id, camera);
		if (!this.activeCameraId) {
			this.setActiveCamera(id);
			if (attachControl) {
				this.attachControl(canvas);
			}
		}
	}

	public setActiveCamera(id: string): void {
		this.activeCameraId = id;
		const camera = this.cameras.get(id);
		if (camera) {
			camera.onProjectionMatrixChangedObservable.add(() =>
				this.observablesManager.onCameraViewUpdated.notifyObservers(camera)
			);
			camera.onViewMatrixChangedObservable.add(() =>
				this.observablesManager.onCameraViewUpdated.notifyObservers(camera)
			);
			this.scene.activeCamera = camera;
		}
	}

	public getCamera(id?: string): ArcRotateCamera {
		if (id) {
			return this.cameras.get(id) as ArcRotateCamera;
		}
		return this.scene.activeCamera as ArcRotateCamera;
	}

	public destroyCamera(id: string): void {
		const camera = this.cameras.get(id);
		if (camera) {
			camera.dispose();
			this.cameras.delete(id);
			if (this.activeCameraId == id) {
				this.activeCameraId = null;
			}
		}
	}

	public configurateCamera(configuaration: CameraConfiguration, id?: string): void {
		Object.entries(configuaration).forEach(([key, value]) => (this.getCamera(id)[key] = value));
	}

	public attachControl(canvas: HTMLCanvasElement): void {
		if (this.scene.activeCamera) {
			this.scene.activeCamera.attachControl(canvas, true);
		}
	}

	public detachControl(canvas: HTMLCanvasElement): void {
		if (this.scene.activeCamera) {
			this.scene.activeCamera.detachControl(canvas);
		}
	}

	public autoFocus(
		boundingExtends: {
			min: { x: number; y: number; z: number };
			max: { x: number; y: number; z: number };
		},
		mobile?: boolean,
		id?: string
	): void {
		const camera = this.getCamera(id);
		if (camera.framingBehavior) {
			const minVector = new Vector3(
				boundingExtends.min.x,
				boundingExtends.min.y,
				boundingExtends.min.z
			);
			const maxVector = new Vector3(
				boundingExtends.max.x,
				boundingExtends.max.y,
				boundingExtends.max.z
			);

			const maximumDistance = Math.max(
				Math.abs(boundingExtends.max.x - boundingExtends.min.x),
				Math.abs(boundingExtends.max.y - boundingExtends.min.y),
				Math.abs(boundingExtends.max.z - boundingExtends.min.z)
			);
			const ratio = this.getScreenRatio();

			const min = 25;
			const max = 75;

			if (maximumDistance > min && ratio > 1.4) {
				const distance = maximumDistance < max ? maximumDistance : max;
				const distanceFactor = distance / max;

				camera.framingBehavior.radiusScale = mobile
					? this.easeOutCubic(distanceFactor, 1, 0.75)
					: this.linear(distanceFactor, 1, 0.6);

				camera.framingBehavior.positionScale = mobile
					? this.easeOutCubic(distanceFactor, 0.5, -0.2)
					: this.linear(distanceFactor, 0.5, -0.2);
			} else {
				camera.framingBehavior.radiusScale = 1;
				camera.framingBehavior.positionScale = 0.5;
			}

			camera.framingBehavior.stopAllAnimations();

			camera.framingBehavior.zoomOnBoundingInfo(minVector, maxVector);
		}
	}

	public resetDefault(props: Array<string>, id?: string): void {
		const config = new Object() as CameraConfiguration;
		props.forEach((prop) => {
			const defaultValue = DEFAULTS[prop];
			if (defaultValue) {
				config[prop] = defaultValue;
			}
		});
		this.configurateCamera(config, id);
	}

	// TODO implement config interface with options for taking screenshot
	public createScreenShot(callback: (data: string) => void, mobile?: boolean, id?: string): void {
		const camera = this.getCamera(id);
		const activeCamera = this.getCamera();
		if (camera.uniqueId !== activeCamera.uniqueId) {
			camera.alpha = activeCamera.alpha;
			camera.beta = activeCamera.beta;
		}
		if (mobile) {
			const ratio = this.getScreenRatio();
			const size = ratio > 1.4 ? { precision: 1.0 } : Math.ceil(window.innerWidth / 100) * 200;
			Tools.CreateScreenshotUsingRenderTarget(this.scene.getEngine(), camera, size, callback);
		} else {
			Tools.CreateScreenshotUsingRenderTarget(
				this.scene.getEngine(),
				camera,
				{ precision: 1.0 },
				callback
			);
		}
	}

	public beginAnimation(
		animations: Array<Animation>,
		start: number,
		end: number,
		callback?: () => void,
		id?: string
	): void {
		const camera = this.getCamera(id);
		this.scene.beginDirectAnimation(camera, animations, start, end, false, 1, callback);
	}

	public stopAnimation(name: string, id?: string): void {
		const camera = this.getCamera(id);
		this.scene.stopAnimation(camera, name);
	}

	private parseConfiguration(configuaration: CameraConfiguration): CameraConfiguration {
		return Object.assign(new Object(), DEFAULTS, configuaration);
	}

	private getScreenRatio(): number {
		const canvasSize = this.scene.getEngine().getRenderingCanvasClientRect();
		if (canvasSize) {
			return canvasSize.width / canvasSize.height;
		} else {
			throw 'CameraManager: getScreenRatio(): canvasSize does not exist.';
		}
	}

	private easeOutCubic(factor: number, from: number, to: number): number {
		const diff = to - from;
		const easing = 1 - Math.pow(1 - factor, 3);
		return from + diff * easing;
	}

	private linear(factor: number, from: number, to: number): number {
		const diff = to - from;
		return from + diff * factor;
	}
}
